import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import type { Result } from '../types/result';
import { normalizeComparisonBounds } from '../utils/comparisonBounds';
import { useComparisonDetailHydration } from './useComparisonDetailHydration';

/**
 * Programs the user has flagged for side-by-side comparison.
 *
 * The provider owns the canonical selection list and a derived
 * `selectedIdsMap` keyed by `programId` for O(1) lookups in render
 * paths and in DSL `_bind` expressions (see "Data binding contract"
 * below).
 *
 * # SAD context (DB-2.2 / EDUN-10690)
 *
 * Per child SAD ADR-14 the provider stack on a Results-page render is:
 *
 * ```
 *   <ComparisonProvider>          ← this provider (top)
 *     <DataBinding / DSM>
 *       <Puck Render>             ← consumes via `_bind` / `_action`
 * ```
 *
 * That positioning lets the binding layer (which lives below) read
 * comparison state through `useComparisonOptional()` without forcing
 * non-Results surfaces (e.g. a standalone Question step) to wrap in
 * a comparison context. The dispatcher seam in
 * `createActionDispatcher` (`toggleComparison` dep) is wired from
 * `ConfigurableStep.tsx` against this provider — when no provider is
 * present the factory keeps its warn-stub registration, matching the
 * "single source of truth" rule from the SAD.
 *
 * # Data binding contract
 *
 * Two surfaces consume comparison state:
 *
 * 1. **Direct React consumers** (the modal, banner, the legacy
 *    `CompareCheckbox`, and the upcoming `DegreeCardDetailed` host
 *    wiring) use `useComparison()` and bind props imperatively.
 * 2. **DSL `_action.comparison.toggle` consumers** dispatch through
 *    the `ActionDispatcher` with `params.item` carrying the full
 *    `Result`-shaped object. The edudirect-admin `action-catalog.ts`
 *    declares `comparison.toggle` with `params.item: { kind:
 *    'string' }` so the picker editor accepts the template
 *    `{{item}}` (an `object`-kind would have routed the author to a
 *    strict JSON textarea that rejects template syntax). At runtime,
 *    `resolveActions.ts` interpolates the single-expression template
 *    and forwards the bound item object verbatim, so
 *    `ConfigurableStep.tsx` receives the full record on
 *    `params.item`, extracts `programId`, calls `togglePrograms`,
 *    and the provider stores the full item so display surfaces
 *    (modal, banner) render school name / image / CTA without
 *    re-fetching.
 * 3. **DSL `_bind` consumers** read the snapshot via
 *    `{{context.comparison.<key>}}` once a host has opted into
 *    publishing the snapshot into `flowContext`. The snapshot shape
 *    is intentionally JSON-serializable and contains only stable
 *    primitive lookups so no function-call grammar is needed in the
 *    binding-resolver:
 *
 *    ```ts
 *    {
 *      selectedIds: number[],
 *      selectedIdsMap: Record<number, true>,
 *      count: number,
 *      maxItems: number,
 *      hasReachedMax: boolean,
 *    }
 *    ```
 *
 *    Authors bind a card's compare checkbox like:
 *    `_bind.compareChecked: "{{context.comparison.selectedIdsMap.{{item.programId}}}}"`
 *    and disable when full like:
 *    `_bind.compareDisabled: "{{context.comparison.hasReachedMax}}"`
 *    paired with a guard expression on the same prop set.
 *
 * # Eventing seam
 *
 * `onAdd` / `onRemove` lifecycle callbacks let TrackingProvider (or a
 * thin wrapper above this provider) emit `CompareAdded` /
 * `CompareRemoved` events without coupling this provider to a
 * specific tracking system. The callbacks fire after the state
 * update is queued so handlers can rely on next-tick context being
 * up to date.
 *
 * # Back-compat
 *
 * The legacy API (`addProgramToCompare`, `removeProgramFromCompare`,
 * `programsToCompare`, `isProgramSelected`, `canAddMorePrograms`,
 * `clearProgramsToCompare`, `toggleComparisonModal`,
 * `setBannerVisible`) is preserved verbatim — the existing
 * `ComparisonBanner`, `ComparisonModal`, `CompareCheckbox`, and
 * Default ResultsPage consumers continue to work without edits.
 */
export interface ComparisonSnapshot {
  readonly selectedIds: readonly number[];
  readonly selectedIdsMap: Readonly<Record<number, true>>;
  readonly count: number;
  readonly minItems: number;
  readonly maxItems: number;
  readonly hasMetMin: boolean;
  readonly hasReachedMax: boolean;
}

export interface ComparisonContextValue {
  // ---------------------------------------------------------------
  // SAD-aligned API (RP-4.1 / DB-2.2)
  // ---------------------------------------------------------------
  /** Snapshot suitable for publication into `flowContext` for `_bind`. */
  readonly snapshot: ComparisonSnapshot;
  /** Canonical min items required to compare (default 2). */
  readonly minItems: number;
  /** Canonical max items (default 3). */
  readonly maxItems: number;
  /** True when `programs.length >= minItems`. */
  readonly hasMetMin: boolean;
  /** True when `programs.length === maxItems`. */
  readonly hasReachedMax: boolean;
  /**
   * Toggle a program in/out of the selection. Returns the resulting
   * checked state (`true` → now selected, `false` → now deselected).
   * No-op + returns current state when adding past `maxItems`.
   */
  togglePrograms: (program: Result) => boolean;
  /** Remove every program from the selection. */
  clear: () => void;

  // ---------------------------------------------------------------
  // Legacy API (preserved for ComparisonBanner / ComparisonModal /
  // CompareCheckbox). New code should prefer the SAD names above.
  // ---------------------------------------------------------------
  programsToCompare: Result[];
  isComparisonModalOpen: boolean;
  isBannerVisible: boolean;
  addProgramToCompare: (program: Result) => boolean;
  removeProgramFromCompare: (programId: number) => void;
  /**
   * Merge `patch` into the stored compare snapshot for `programId`.
   * Used by comparison detail hydration (TCPA / drawer-detail fields)
   * after an independent offering by-id fetch. No-op when the id is
   * not currently selected.
   */
  patchProgramInCompare: (programId: number, patch: Partial<Result>) => void;
  clearProgramsToCompare: () => void;
  toggleComparisonModal: (open: boolean) => void;
  setBannerVisible: (visible: boolean) => void;
  canAddMorePrograms: boolean;
  isProgramSelected: (programId: number) => boolean;

  // ---------------------------------------------------------------
  // DSL correlation
  // ---------------------------------------------------------------
  /**
   * DSL result-step config id, present only when the provider is
   * mounted on the DSL result page (`ConfigurableStep` passes
   * `dslConfig.configId`). Comparison analytics emitters
   * (`compareAnalytics` add/remove, `ComparisonModal` viewed) read this
   * so their beams carry `dslConfigId` on the DSL path. `undefined` on
   * the imperative path → those beams stay byte-identical.
   */
  readonly dslConfigId?: string;
}

const ComparisonContext = createContext<ComparisonContextValue | undefined>(
  undefined,
);

interface ComparisonProviderProps {
  children: ReactNode;
  /** Default 2 to match the comparison UX requirement. */
  minPrograms?: number;
  /** Default 3 to match legacy behavior + DSL author expectation. */
  maxPrograms?: number;
  /** Optional eventing seam — fires after a successful add. */
  onAdd?: (program: Result) => void;
  /** Optional eventing seam — fires after a successful remove. */
  onRemove?: (programId: number) => void;
  /**
   * DSL result-step config id. Forwarded onto the context so comparison
   * analytics emitters append `dslConfigId` on the DSL result page.
   * Omitted by imperative callers.
   */
  dslConfigId?: string;
}

export const ComparisonProvider: FC<ComparisonProviderProps> = ({
  children,
  minPrograms,
  maxPrograms,
  onAdd,
  onRemove,
  dslConfigId,
}) => {
  const [programsToCompare, setProgramsToCompare] = useState<Result[]>([]);
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const { minItems: normalizedMinPrograms, maxItems: normalizedMaxPrograms } =
    normalizeComparisonBounds({
      minItems: minPrograms,
      maxItems: maxPrograms,
    });

  // Refs so the dispatcher-side `toggleComparison` callback (memoized
  // once at mount inside ConfigurableStep) always sees the latest
  // selection without forcing the dispatcher to rebuild on every
  // selection change.
  const programsRef = useRef(programsToCompare);
  programsRef.current = programsToCompare;
  const onAddRef = useRef(onAdd);
  onAddRef.current = onAdd;
  const onRemoveRef = useRef(onRemove);
  onRemoveRef.current = onRemove;

  const addProgramToCompare = useCallback(
    (program: Result): boolean => {
      if (programsRef.current.length >= normalizedMaxPrograms) {
        return false;
      }
      if (programsRef.current.some((p) => p.programId === program.programId)) {
        return false;
      }
      setProgramsToCompare((prev) => [...prev, program]);
      onAddRef.current?.(program);
      return true;
    },
    [normalizedMaxPrograms],
  );

  const removeProgramFromCompare = useCallback((programId: number) => {
    // Pre-check against the ref so the eventing callback fires only
    // when the id was actually selected. Doing the diff inside the
    // updater would race with React 18's batched scheduling — the
    // updater does not run synchronously, so a closure-captured
    // `removed` flag is still `false` at the time we'd consult it.
    const exists = programsRef.current.some((p) => p.programId === programId);
    if (!exists) {
      return;
    }
    setProgramsToCompare((prev) =>
      prev.filter((p) => p.programId !== programId),
    );
    onRemoveRef.current?.(programId);
  }, []);

  const patchProgramInCompare = useCallback(
    (programId: number, patch: Partial<Result>) => {
      const exists = programsRef.current.some((p) => p.programId === programId);
      if (!exists) {
        return;
      }
      setProgramsToCompare((prev) =>
        prev.map((program) =>
          program.programId === programId ? { ...program, ...patch } : program,
        ),
      );
    },
    [],
  );

  const clearProgramsToCompare = useCallback(() => {
    setProgramsToCompare([]);
  }, []);

  const toggleComparisonModal = useCallback((open: boolean) => {
    setIsComparisonModalOpen(open);
  }, []);

  const setBannerVisible = useCallback((visible: boolean) => {
    setIsBannerVisible(visible);
  }, []);

  const isProgramSelected = useCallback(
    (programId: number): boolean =>
      programsToCompare.some((p) => p.programId === programId),
    [programsToCompare],
  );

  const togglePrograms = useCallback(
    (program: Result): boolean => {
      const isCurrentlySelected = programsRef.current.some(
        (p) => p.programId === program.programId,
      );
      if (isCurrentlySelected) {
        removeProgramFromCompare(program.programId);
        return false;
      }
      const added = addProgramToCompare(program);
      // If we hit the max, the selection didn't change.
      return added;
    },
    [addProgramToCompare, removeProgramFromCompare],
  );

  useEffect(() => {
    if (programsRef.current.length <= normalizedMaxPrograms) {
      return;
    }
    setProgramsToCompare((prev) => prev.slice(0, normalizedMaxPrograms));
  }, [normalizedMaxPrograms]);

  useComparisonDetailHydration(programsToCompare, patchProgramInCompare);

  const snapshot: ComparisonSnapshot = useMemo(() => {
    const selectedIds = programsToCompare.map((p) => p.programId);
    const selectedIdsMap: Record<number, true> = {};
    for (const id of selectedIds) {
      selectedIdsMap[id] = true;
    }
    return {
      selectedIds,
      selectedIdsMap,
      count: selectedIds.length,
      minItems: normalizedMinPrograms,
      maxItems: normalizedMaxPrograms,
      hasMetMin: selectedIds.length >= normalizedMinPrograms,
      hasReachedMax: selectedIds.length >= normalizedMaxPrograms,
    };
  }, [programsToCompare, normalizedMinPrograms, normalizedMaxPrograms]);

  const canAddMorePrograms = programsToCompare.length < normalizedMaxPrograms;

  const value: ComparisonContextValue = useMemo(
    () => ({
      // SAD-aligned
      snapshot,
      minItems: normalizedMinPrograms,
      maxItems: normalizedMaxPrograms,
      hasMetMin: snapshot.hasMetMin,
      hasReachedMax: snapshot.hasReachedMax,
      togglePrograms,
      clear: clearProgramsToCompare,

      // Legacy
      programsToCompare,
      isComparisonModalOpen,
      isBannerVisible,
      addProgramToCompare,
      removeProgramFromCompare,
      patchProgramInCompare,
      clearProgramsToCompare,
      toggleComparisonModal,
      setBannerVisible,
      canAddMorePrograms,
      isProgramSelected,
      dslConfigId,
    }),
    [
      snapshot,
      dslConfigId,
      normalizedMinPrograms,
      normalizedMaxPrograms,
      togglePrograms,
      programsToCompare,
      isComparisonModalOpen,
      isBannerVisible,
      addProgramToCompare,
      removeProgramFromCompare,
      patchProgramInCompare,
      clearProgramsToCompare,
      toggleComparisonModal,
      setBannerVisible,
      canAddMorePrograms,
      isProgramSelected,
    ],
  );

  return (
    <ComparisonContext.Provider value={value}>
      {children}
    </ComparisonContext.Provider>
  );
};

/**
 * Returns the active comparison context — throws when called outside
 * a `ComparisonProvider` (legacy contract retained for back-compat).
 */
export const useComparison = (): ComparisonContextValue => {
  const context = useContext(ComparisonContext);
  if (context === undefined) {
    throw new Error('useComparison must be used within a ComparisonProvider');
  }
  return context;
};

/**
 * Tolerant variant — returns `undefined` when no provider is mounted.
 * Use this from infrastructure code that runs across surfaces with
 * and without a Comparison context (the prime example is
 * `ConfigurableStep`'s `createActionDispatcher` wiring, which has to
 * supply `toggleComparison` only when a provider is actually in
 * scope so the dispatcher's warn-stub remains active otherwise), as
 * well as from optional-compare UI surfaces such as `Card/Default`.
 */
export const useComparisonOptional = (): ComparisonContextValue | undefined =>
  useContext(ComparisonContext);
